import { Link } from "react-router-dom";
import PageTransition from "../components/PageTransition";
import { LazyBackgroundImage } from "../../shared/components";

const About: React.FC = () => {
    return (
        <PageTransition>
        <div className="bg-gradient-to-b from-transparent via-transparent to-transparent dark:bg-gray-900">
            {/* About the Author Hero Section */}
            <div className="relative overflow-hidden bg-gradient-to-b from-brand-50 to-yellow-25 dark:from-gray-800 dark:to-gray-900">
                {/* Vines Background Image - Lazy Loaded */}
                <LazyBackgroundImage
                    webpSrc="/images/optimized/vines_top.webp"
                    fallbackSrc="/images/vines_top.png"
                    className="absolute inset-x-0 top-0 h-80 bg-cover bg-top bg-center bg-no-repeat opacity-50"
                    rootMargin="100px"
                />
                <div className="relative z-10 mx-auto max-w-7xl px-4 pt-16 pb-12 sm:px-6 lg:px-8 sm:pt-24 sm:pb-24 lg:pb-32">
                    <div className="text-center mb-12">
                        <h1 className="text-4xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-5xl mb-3">
                            Artist Bio
                        </h1>
                    </div>

                    {/* Author Content - Two Column Layout */}
                    <div className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
                        {/* Left Column - Author Image */}
                        <div className="lg:col-span-4 flex justify-center">
                            <div className="relative">
                                <div className="w-64 h-64 sm:w-80 sm:h-80 rounded-full overflow-hidden shadow-2xl ring-4 ring-white/50 dark:ring-gray-700/50">
                                    <picture>
                                        <source srcSet="/images/optimized/paula.webp" type="image/webp" />
                                        <img
                                            src="/images/paula.png"
                                            alt="Author portrait"
                                            className="w-full h-full object-cover"
                                            loading="lazy"
                                        />
                                    </picture>
                                </div>
                            </div>
                        </div>

                        {/* Right Column - Author Bio */}
                        <div className="lg:col-span-8 space-y-6">
                            <div className="bg-white/60 dark:bg-gray-800/60 backdrop-blur-sm rounded-2xl p-8 shadow-lg">
                                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                                    Before There Were Petals
                                </h2>
                                <div className="space-y-4 text-gray-700 dark:text-gray-300 text-lg leading-relaxed">
                                    <p>
                                        If you had to assign elementary school jobs to women in a police lineup,
                                        you'd point straight at me and bark into the microphone.
                                    </p>
                                    <p>
                                        "Number three with the wrinkles&mdash;step forward."
                                    </p>
                                    <p>
                                        I'd zombie-shuffle three steps into the spotlight.<br />
                                        Confidently, you'd announce, "You're the librarian."<br />
                                        Wrong. I can't alphabetize to save my life.<br />
                                        "Recess monitor? Lunch lady? Janitor? Kindergarten teacher?"
                                    </p>
                                    <p>
                                        I'd lose the kids, eat the food, hate cleaning&mdash;and leading a class
                                        would feel like giving a sightseeing tour with my eyes closed.
                                    </p>
                                    <p>
                                        "So who are you?"<br />
                                        The P.E. teacher.
                                    </p>
                                    <p>
                                        Here's the twist: I landed the job in my fifties. I didn't play a lick of
                                        sports in high school. My knees creak louder than the gym floor. Yet somehow
                                        I'm thriving in what I never knew was my dream job.
                                    </p>
                                    <p>
                                        I'm also dyslexic. I have a rote-memory disability. My attention wanders off
                                        without telling me where it's going&mdash;or when it'll be back. If I'm
                                        holding something, give me five minutes… and I'll be looking for it.
                                    </p>
                                    <p>
                                        For years I tried to survive in jobs that demanded everything I wasn't:
                                        precision, recall, flawless accuracy. Switchboard operator. Waitress. Clerk.
                                        Dental assistant. Purchasing supervisor.
                                    </p>
                                    <p>
                                        I either got fired or barely hung on.<br />
                                        I thought my brain was the problem.<br />
                                        It wasn't.<br />
                                        It just couldn't grow where I kept planting it.
                                    </p>
                                    <p>
                                        Naturally&mdash;with absolutely no prior experience&mdash;I started painting
                                        with my neighbor. I was in my fifties. She was in her seventies.
                                    </p>
                                    <p>
                                        My favorite thing to paint? Flowers. I quickly realized my brush refused to
                                        paint fine details. But this time I paid attention to the clues. Instead of
                                        forcing the impossible, I leaned into my strengths: a bold, blocky folk style.
                                    </p>
                                    <p>
                                        Final confession: I'm not a botanist. Decades ago I took one botany class in
                                        college. That barely qualifies me to mop the lobby of the science building.
                                    </p>
                                    <p>
                                        No formal training in art, botany, or psychology. Yet my unconventional brain
                                        connects unlikely dots into metaphors. Everything changed when I stopped
                                        trying to be what I wasn't and leaned into what I am.
                                    </p>
                                    <p>
                                        That realization eventually grew into <em>The Brilliance of Blossoms</em>.
                                    </p>
                                    <p>
                                        My hope is that you discover the brilliance already rooted in
                                        you… and blossom.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* CTA */}
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <div className="mb-12 bg-gradient-to-r from-brand-500 to-lavender-400 dark:bg-brand-800 rounded-2xl shadow-xl p-8 sm:p-12 text-center">
                    <h2 className="text-3xl font-bold text-white mb-4">
                        Ready to Discover Your Flower Personality?
                    </h2>
                    <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto">
                        Take our interactive quiz to find out which flower embodies your unique adaptability
                        traits and receive personalized insights for your growth journey.
                    </p>
                    <Link
                        to="/quiz"
                        className="inline-block rounded-md bg-white px-8 py-4 text-lg font-semibold text-brand-600 shadow-lg hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white transition-all transform hover:scale-105"
                    >
                        Take the Quiz Now
                    </Link>
                </div>
            </div>
        </div>
        </PageTransition>
    );
};

export default About;
